export type WorkspaceKeywordBidMode =
  | 'SET'
  | 'INCREASE_AMOUNT'
  | 'INCREASE_PERCENT'
  | 'DECREASE_AMOUNT'
  | 'DECREASE_PERCENT'
  | 'SET_SUGGESTED';

/** Modes the Change Bid dropdown offers. Suggested bids are a separate action. */
export type WorkspaceKeywordBidAdjustMode = Exclude<WorkspaceKeywordBidMode, 'SET_SUGGESTED'>;

export type WorkspaceKeywordBidJobStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'completed_with_errors'
  | 'failed';

export type WorkspaceKeywordBidResultStatus = 'pending' | 'applied' | 'skipped' | 'failed';

export type WorkspaceKeywordBidJob = {
  id: string;
  profileId: string;
  status: WorkspaceKeywordBidJobStatus;
  mode: WorkspaceKeywordBidMode;
  value: number | null;
  selection: Record<string, unknown>;
  totals: {
    total?: number;
    applied?: number;
    failed?: number;
    skipped?: number;
    pending?: number;
  };
  applyProgress: {
    processed?: number;
    total?: number;
    error?: string;
  };
  startedAt: string | null;
  finishedAt: string | null;
  createdByUserId: string | null;
  createdAt: string | null;
};

export type WorkspaceKeywordBidResult = {
  id: string;
  jobId: string;
  keywordId: string;
  adProduct: string;
  campaignId: string | null;
  adGroupId: string | null;
  keywordText: string | null;
  previousBid: number | null;
  proposedBid: number | null;
  status: WorkspaceKeywordBidResultStatus;
  errorCode: string | null;
  errorMessage: string | null;
  appliedAt: string | null;
  retryCount: number;
};

export type WorkspaceKeywordBidJobCreateBody = {
  mode: WorkspaceKeywordBidMode;
  /** Omitted for SET_SUGGESTED. Each keyword uses its own Amazon suggested bid. */
  value?: number;
  keywordIds?: string[];
  selectAllMatching?: boolean;
  startDate?: string;
  endDate?: string;
  appliedFilter?: Record<string, unknown>;
  columnFilters?: {
    search?: string;
    status?: string;
    matchType?: string;
  };
};

export const WORKSPACE_KEYWORD_BID_MODE_LABELS: Record<WorkspaceKeywordBidAdjustMode, string> = {
  SET: 'Set bid to',
  INCREASE_AMOUNT: 'Increase bid by value',
  INCREASE_PERCENT: 'Increase by %',
  DECREASE_AMOUNT: 'Decrease bid by value',
  DECREASE_PERCENT: 'Decrease by %',
};

export const WORKSPACE_KEYWORD_BID_TERMINAL_STATUSES = new Set<WorkspaceKeywordBidJobStatus>([
  'completed',
  'completed_with_errors',
  'failed',
]);

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

export function parseWorkspaceKeywordBidJob(data: unknown): WorkspaceKeywordBidJob | null {
  const raw = asRecord(data);
  const id = asString(raw.id);
  const profileId = asString(raw.profileId) ?? asString(raw.profile_id);
  const status = asString(raw.status) as WorkspaceKeywordBidJobStatus | null;
  if (!id || !profileId || !status) return null;
  const totals = asRecord(raw.totals);
  const progress = asRecord(raw.applyProgress ?? raw.apply_progress);
  return {
    id,
    profileId,
    status,
    mode: (asString(raw.mode) as WorkspaceKeywordBidMode) || 'SET',
    value: asNumber(raw.value),
    selection: asRecord(raw.selection),
    totals: {
      total: asNumber(totals.total) ?? undefined,
      applied: asNumber(totals.applied) ?? undefined,
      failed: asNumber(totals.failed) ?? undefined,
      skipped: asNumber(totals.skipped) ?? undefined,
      pending: asNumber(totals.pending) ?? undefined,
    },
    applyProgress: {
      processed: asNumber(progress.processed) ?? undefined,
      total: asNumber(progress.total) ?? undefined,
      error: asString(progress.error) ?? undefined,
    },
    startedAt: asString(raw.startedAt) ?? asString(raw.started_at),
    finishedAt: asString(raw.finishedAt) ?? asString(raw.finished_at),
    createdByUserId: asString(raw.createdByUserId) ?? asString(raw.created_by_user_id),
    createdAt: asString(raw.createdAt) ?? asString(raw.created_at),
  };
}

export function parseWorkspaceKeywordBidResult(data: unknown): WorkspaceKeywordBidResult | null {
  const raw = asRecord(data);
  const id = asString(raw.id);
  const keywordId = asString(raw.keywordId) ?? asString(raw.keyword_id);
  const status = asString(raw.status) as WorkspaceKeywordBidResultStatus | null;
  if (!id || !keywordId || !status) return null;
  return {
    id,
    jobId: asString(raw.jobId) ?? asString(raw.job_id) ?? '',
    keywordId,
    adProduct: asString(raw.adProduct) ?? asString(raw.ad_product) ?? 'SP',
    campaignId: asString(raw.campaignId) ?? asString(raw.campaign_id),
    adGroupId: asString(raw.adGroupId) ?? asString(raw.ad_group_id),
    keywordText: asString(raw.keywordText) ?? asString(raw.keyword_text),
    previousBid: asNumber(raw.previousBid) ?? asNumber(raw.previous_bid),
    proposedBid: asNumber(raw.proposedBid) ?? asNumber(raw.proposed_bid),
    status,
    errorCode: asString(raw.errorCode) ?? asString(raw.error_code),
    errorMessage: asString(raw.errorMessage) ?? asString(raw.error_message),
    appliedAt: asString(raw.appliedAt) ?? asString(raw.applied_at),
    retryCount: asNumber(raw.retryCount) ?? asNumber(raw.retry_count) ?? 0,
  };
}
